import { useLayoutEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { gsap } from '../../anim/gsapSetup'
import { Icon } from '../common/Icon'
import { ProductImage } from './ProductImage'
import { categoryBySlug } from '../../lib/categories'
import { primaryVariant, productImageAlt } from '../../lib/image'
import { accentAlpha, onLight } from '../../lib/slug'
import { cn } from '../../lib/cn'
import type { Product } from '../../types'

interface ProductCardProps {
  product: Product
  priority?: boolean
  className?: string
}

export function ProductCard({ product, priority = false, className }: ProductCardProps) {
  const ref = useRef<HTMLElement>(null)
  const accent = product.accentColor
  const category = categoryBySlug(product.categorySlug)
  const variant = primaryVariant(product)

  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    // pointer-aware tilt only where a real pointer + motion are available
    const canHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (!canHover || reduced) return

    const stage = el.querySelector<HTMLElement>('[data-card="stage"]')
    const img = el.querySelector<HTMLElement>('[data-card="img"]')
    const glow = el.querySelector<HTMLElement>('[data-card="glow"]')
    const ctx = gsap.context(() => {
      const rx = gsap.quickTo(stage, 'rotationX', { duration: 0.5, ease: 'power2.out' })
      const ry = gsap.quickTo(stage, 'rotationY', { duration: 0.5, ease: 'power2.out' })
      const ix = gsap.quickTo(img, 'xPercent', { duration: 0.6, ease: 'power2.out' })
      const iy = gsap.quickTo(img, 'yPercent', { duration: 0.6, ease: 'power2.out' })

      const onMove = (e: MouseEvent) => {
        const r = el.getBoundingClientRect()
        const px = (e.clientX - r.left) / r.width - 0.5
        const py = (e.clientY - r.top) / r.height - 0.5
        ry(px * 10)
        rx(-py * 10)
        ix(px * 6)
        iy(-py * 6)
        if (glow) gsap.set(glow, { left: `${(px + 0.5) * 100}%`, top: `${(py + 0.5) * 100}%` })
      }
      const onLeave = () => {
        rx(0); ry(0); ix(0); iy(0)
      }
      el.addEventListener('mousemove', onMove)
      el.addEventListener('mouseleave', onLeave)
      return () => {
        el.removeEventListener('mousemove', onMove)
        el.removeEventListener('mouseleave', onLeave)
      }
    }, el)
    return () => ctx.revert()
  }, [])

  return (
    <article
      ref={ref}
      data-flip-id={product.slug}
      className={cn('product-card group [perspective:1200px]', className)}
    >
      <Link
        to={`/catalog/${product.slug}`}
        className="relative flex h-full flex-col overflow-hidden rounded-lg border border-sand-200 bg-white shadow-soft transition-shadow duration-500 hover:shadow-lift focus-visible:shadow-lift"
        aria-label={`${product.name} — view product details`}
      >
        {/* stage */}
        <div
          data-card="stage"
          className="relative aspect-[4/3.4] overflow-hidden [transform-style:preserve-3d]"
          style={{ background: `radial-gradient(120% 100% at 50% 8%, ${accentAlpha(accent, 0.14)}, #fbfaf6 62%)` }}
        >
          <div
            data-card="glow"
            aria-hidden="true"
            className="pointer-events-none absolute h-40 w-40 -translate-x-1/2 -translate-y-1/2 rounded-full opacity-0 blur-2xl transition-opacity duration-500 group-hover:opacity-100"
            style={{ left: '50%', top: '35%', background: accentAlpha(accent, 0.5) }}
          />
          <span
            className="absolute left-4 top-4 z-20 inline-flex items-center gap-1.5 rounded-full bg-white/85 px-3 py-1 text-[0.68rem] font-semibold text-ink-700 shadow-sm backdrop-blur"
          >
            <span className="h-1.5 w-1.5 rounded-full" style={{ background: accent }} />
            {category?.name}
          </span>
          <div data-card="img" className="absolute inset-0 flex items-center justify-center p-6 will-change-transform">
            <ProductImage
              src={variant.image}
              alt={productImageAlt(product, variant)}
              accent={accent}
              name={product.name}
              priority={priority}
              className="h-full w-full transition-transform duration-500 group-hover:scale-[1.04]"
            />
          </div>
        </div>

        {/* body */}
        <div className="flex flex-1 flex-col gap-3 p-6">
          <div>
            <h3 className="font-display text-xl font-semibold text-ink">{product.name}</h3>
            <p className="mt-0.5 text-sm font-medium" style={{ color: onLight(accent) }}>
              {product.tagline}
            </p>
          </div>
          <p className="text-[0.95rem] leading-relaxed text-ink-500">{product.purpose}</p>

          <div className="mt-1 flex flex-wrap gap-1.5">
            {product.variants.map((v) => (
              <span
                key={v.id}
                className="rounded-full border border-sand-200 bg-canvas px-2.5 py-1 text-xs font-medium text-ink-700"
              >
                {v.packSize}
              </span>
            ))}
          </div>

          <span className="mt-auto inline-flex items-center gap-1.5 pt-2 text-sm font-semibold text-brand-600">
            View details
            <Icon
              name="ArrowRight"
              className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1"
            />
          </span>
        </div>
      </Link>
    </article>
  )
}
